import { useCallback, useEffect, useMemo, useState } from "react";
import { Table, Dropdown, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import Loader from "../../../../Components/Loader";
import ClearFilterButton from "../../../../Components/ClearFilterButton";
import TablePagination from "../../../../Components/AdministrativeSettings/TablePagination";
import NoListDataAvailable from "../../../../Components/NoListDataAvailable";
import ConfirmationModal from "../../../../Components/ConfirmationModal";
import Toaster from "../../../../Components/Toaster";
import navdots from "../../../../Assets/images/navdots.svg";
import AdminStatusDropdown from "../../../../Components/AdministrativeSettings/AdminStatusDropdown";
import SearchInput from "../../../../Components/AdministrativeSettings/SearchInput";
import { RequestorService } from "../../../../Services/Requestor.Service";
import TokenService from "../../../../Services/Token.service";

const US_STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California",
    "Colorado", "Connecticut", "Delaware", "Florida", "Georgia",
    "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas",
    "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts",
    "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana",
    "Nebraska", "Nevada", "New Hampshire", "New Jersey",
    "New Mexico", "New York", "North Carolina", "North Dakota",
    "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island",
    "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah",
    "Vermont", "Virginia", "Washington", "West Virginia",
    "Wisconsin", "Wyoming"
];

const campusStatuses = [
    { id: 1, label: "Active", value: 1 },
    { id: 0, label: "Inactive", value: 0 },
];

const emptyFilters = {
    campus_name: "",
    pharma_name: "",
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    postal_code: "",
};

function AddCampus() {
    const navigate = useNavigate();
    const previousUrl = TokenService.getPrevLink();
    const [loading, setLoading] = useState(false);
    const [campusList, setCampusList] = useState([]);
    const [totalCampus, setTotalCampus] = useState(0);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [filters, setFilters] = useState({ ...emptyFilters });
    const [searchVisibility, setSearchVisibility] = useState({
        campus_name: false,
        pharma_name: false,
        address_1: false,
        address_2: false,
        city: false,
        state: false,
        postal_code: false,
    });
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pagination, setPagination] = useState({ limit: 10, offset: 0, page: 1 });
    const [filterCount, setFilterCount] = useState(0);
    const [toastState, setToastState] = useState({ show: false, message: "", variant: "" });
    const [editingCampus, setEditingCampus] = useState(null);
    const [actionModal, setActionModal] = useState({ type: null, campus: null });
    const [pharmaOptions, setPharmaOptions] = useState([]);
    const [stateSearch, setStateSearch] = useState("");

    const totalPages = useMemo(() => {
        if (rowsPerPage === 0) {
            return 1;
        }
        const pages = Math.ceil(totalCampus / rowsPerPage);
        return pages > 0 ? pages : 1;
    }, [rowsPerPage, totalCampus]);

    const filteredStates = useMemo(() => {
        return US_STATES.filter((state) =>
            state.toLowerCase().includes(stateSearch.toLowerCase())
        );
    }, [stateSearch]);

    const closeToast = () => setToastState({ show: false, message: "", variant: "" });
    const setToast = (message, variant) => {
        setToastState({ show: true, message, variant });
    };

    const handleRowsPerPageChange = (value) => {
        if (value === "Show All") {
            const allRows = totalCampus || 10;
            setRowsPerPage(allRows);
            setPagination({ limit: allRows, offset: 0, page: 1 });
        } else {
            const numericValue = Number(value);
            setRowsPerPage(numericValue);
            setPagination({ limit: numericValue, offset: 0, page: 1 });
        }
    };

    const handleNextPage = () => {
        setPagination({
            limit: rowsPerPage,
            offset: pagination.page * rowsPerPage,
            page: pagination.page + 1,
        });
    };

    const handlePreviousPage = () => {
        setPagination({
            limit: rowsPerPage,
            offset: (pagination.page - 2) * rowsPerPage,
            page: pagination.page - 1,
        });
    };

    const handleFilterInputChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination({ limit: rowsPerPage, offset: 0, page: 1 });
    };

    const handleClearFilters = () => {
        setFilters({ ...emptyFilters });
        setSelectedStatuses([]);
        setSearchVisibility({
            campus_name: false,
            pharma_name: false,
            address_1: false,
            address_2: false,
            city: false,
            state: false,
            postal_code: false,
        });
        setPagination({ limit: rowsPerPage, offset: 0, page: 1 });
    };

    const handleStatusFilterChange = (status) => {
        if (selectedStatuses.includes(status)) {
            setSelectedStatuses(selectedStatuses.filter((selected) => selected !== status));
        } else {
            setSelectedStatuses([...selectedStatuses, status]);
        }
        setPagination({ limit: rowsPerPage, offset: 0, page: 1 });
    };

    const fetchPharmaOptions = useCallback(() => {
        const payload = {
            limit: 1000,
            offset: 0,
            filters: [{ status: [1] }],
        };
        RequestorService.getPharmaList(payload)
            .then((response) => {
                const data = response?.data || response || [];
                let list = [];
                if (Array.isArray(data)) {
                    list = data;
                } else if (Array.isArray(data.list)) {
                    list = data.list;
                }
                setPharmaOptions(list);
            })
            .catch((error) => {
                console.error("Failed to fetch pharma options:", error);
                setPharmaOptions([]);
            });
    }, []);

    const fetchCampusList = useCallback(() => {
        setLoading(true);

        const filtersPayload = {
            campus_name: filters.campus_name?.trim(),
            pharma_name: filters.pharma_name?.trim(),
            address_1: filters.address_1?.trim(),
            address_2: filters.address_2?.trim(),
            city: filters.city?.trim(),
            state: filters.state?.trim(),
            postal_code: filters.postal_code?.trim(),
            status: selectedStatuses,
        };

        const payload = {
            limit: pagination.limit,
            offset: pagination.offset,
            filters: [filtersPayload],
        };

        RequestorService.getCampusList(payload)
            .then((response) => {
                const data = response?.data || response || {};
                let list = [];
                if (Array.isArray(data)) {
                    list = data;
                } else if (Array.isArray(data.list)) {
                    list = data.list;
                }
                setCampusList(list);
                setTotalCampus(response?.total ?? data.total ?? list.length ?? 0);
            })
            .catch((error) => {
                console.error("Failed to fetch campus list:", error);
                setCampusList([]);
                setTotalCampus(0);
            })
            .finally(() => setLoading(false));
    }, [filters, pagination, selectedStatuses]);

    useEffect(() => {
        fetchPharmaOptions();
    }, [fetchPharmaOptions]);

    useEffect(() => {
        fetchCampusList();
    }, [fetchCampusList]);

    useEffect(() => {
        const filterTotal =
            (selectedStatuses.length > 0 ? 1 : 0) +
            Object.values(filters).filter((val) => val && val.trim() !== "").length;
        setFilterCount(filterTotal);
    }, [filters, selectedStatuses]);

    const formik = useFormik({
        initialValues: {
            pharma_id: "",
            campus_name: "",
            address_1: "",
            address_2: "",
            city: "",
            state: "",
            postal_code: "",
        },
        validationSchema: Yup.object({
            pharma_id: Yup.string().required("Pharma is required"),
            campus_name: Yup.string().trim().required("Campus name is required"),
            address_1: Yup.string().trim().required("Street address 1 is required"),
            address_2: Yup.string().trim().nullable(),
            city: Yup.string().trim().required("City is required"),
            state: Yup.string().required("State is required"),
            postal_code: Yup.string()
                .matches(/^\d{5}$/, "Enter a valid 5-digit postal code")
                .required("Postal code is required"),
        }),
        onSubmit: async (values, { resetForm, setSubmitting }) => {
            try {
                const payload = {
                    pharma_id: Number(values.pharma_id),
                    campus_name: values.campus_name.trim(),
                    address_1: values.address_1.trim(),
                    address_2: values.address_2?.trim() || null,
                    city: values.city.trim(),
                    state: values.state,
                    postal_code: values.postal_code.trim(),
                };

                const response = editingCampus
                    ? await RequestorService.editCampus({ ...payload, campus_id: editingCampus.id || editingCampus.campus_id })
                    : await RequestorService.addCampus(payload);

                if (response?.status) {
                    setToast(response.message || "Campus saved successfully.", "success");
                    resetForm();
                    setEditingCampus(null);
                    setPagination({ limit: rowsPerPage, offset: 0, page: 1 });
                } else {
                    setToast(response?.message || "Unable to save campus.", "danger");
                }
            } catch (error) {
                console.error("Failed to submit campus form:", error);
                setToast("Failed to save campus. Please try again.", "danger");
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleEditCampus = (campus) => {
        setEditingCampus(campus);
        formik.setValues({
            pharma_id: campus.pharma_id ? String(campus.pharma_id) : "",
            campus_name: campus.campus_name || "",
            address_1: campus.campus_street1 || "",
            address_2: campus.campus_street2 || "",
            city: campus.campus_city || "",
            state: campus.campus_state || "",
            postal_code: campus.campus_postal_code || "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleCancelEditing = () => {
        setEditingCampus(null);
        formik.resetForm();
        navigate(`${previousUrl || "/admin/pharma-management"}`);
    };

    const handleUpdateCampusStatus = async (statusValue) => {
        if (!actionModal.campus) {
            return;
        }
        let willRefetch = false;
        try {
            setLoading(true);
            const response = await RequestorService.updateCampusStatus({
                campus_id: actionModal.campus.id || actionModal.campus.campus_id,
                status: statusValue,
            });

            if (response?.status) {
                setToast(response.message || "Campus status updated.", "success");
                willRefetch = true;
                fetchCampusList();
            } else {
                setToast(response?.message || "Unable to update campus status.", "danger");
            }
        } catch (error) {
            console.error("Failed to update campus status:", error);
            setToast("Failed to update campus status. Please try again.", "danger");
        } finally {
            setActionModal({ type: null, campus: null });
            if (!willRefetch) {
                setLoading(false);
            }
        }
    };

    const openStatusModal = (campus) => setActionModal({ type: "status", campus });
    const openDeleteModal = (campus) => setActionModal({ type: "delete", campus });
    const closeModal = () => setActionModal({ type: null, campus: null });

    const isCampusActive = (campus) =>
        campus?.campus_status === "1" ||
        campus?.status === 1 ||
        campus?.active === 1 ||
        campus?.active === true;

    const confirmModalAction = () => {
        if (!actionModal.campus) {
            return;
        }
        if (actionModal.type === "delete") {
            handleUpdateCampusStatus(2);
        } else {
            const currentStatus = isCampusActive(actionModal.campus) ? 1 : 0;
            const nextStatus = currentStatus === 1 ? 0 : 1;
            handleUpdateCampusStatus(nextStatus);
        }
    };

    return (
        <>
            <Loader loading={loading} />
            <div className="el-middle-wrapper">
                <Link className="back-link" to={`${previousUrl}`}><i className='el-icon-back'></i> Pharma Management</Link>
                <div className="el-bg-table mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3>{editingCampus ? "Edit Campus" : "Add Campus"}</h3>
                        {editingCampus && (
                            <span className="text-muted">Editing: {editingCampus.campus_name}</span>
                        )}
                    </div>
                    <form onSubmit={formik.handleSubmit}>
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label">
                                    Select Pharma <span className="text-danger">*</span>
                                </label>
                                <select
                                    className={`form-select ${formik.touched.pharma_id && formik.errors.pharma_id ? "is-invalid" : ""}`}
                                    name="pharma_id"
                                    value={formik.values.pharma_id}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                >
                                    <option value="">Select Pharma</option>
                                    {pharmaOptions.map((pharma) => (
                                        <option key={pharma.id} value={pharma.id}>
                                            {pharma.pharma_name}
                                        </option>
                                    ))}
                                </select>
                                {formik.touched.pharma_id && formik.errors.pharma_id && (
                                    <div className="invalid-feedback">{formik.errors.pharma_id}</div>
                                )}
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">
                                    Campus Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${formik.touched.campus_name && formik.errors.campus_name ? "is-invalid" : ""}`}
                                    placeholder="Enter campus name"
                                    {...formik.getFieldProps("campus_name")}
                                />
                                {formik.touched.campus_name && formik.errors.campus_name && (
                                    <div className="invalid-feedback">{formik.errors.campus_name}</div>
                                )}
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">
                                    Street Address 1 <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${formik.touched.address_1 && formik.errors.address_1 ? "is-invalid" : ""}`}
                                    placeholder="Enter street address 1"
                                    {...formik.getFieldProps("address_1")}
                                />
                                {formik.touched.address_1 && formik.errors.address_1 && (
                                    <div className="invalid-feedback">{formik.errors.address_1}</div>
                                )}
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Street Address 2</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter street address 2"
                                    {...formik.getFieldProps("address_2")}
                                />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">
                                    City <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${formik.touched.city && formik.errors.city ? "is-invalid" : ""}`}
                                    placeholder="Enter city"
                                    {...formik.getFieldProps("city")}
                                />
                                {formik.touched.city && formik.errors.city && (
                                    <div className="invalid-feedback">{formik.errors.city}</div>
                                )}
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">
                                    State <span className="text-danger">*</span>
                                </label>
                                <Dropdown className="w-100">
                                    <Dropdown.Toggle
                                        variant="light"
                                        className={`w-100 text-start form-control ${formik.touched.state && formik.errors.state ? "is-invalid" : ""}`}
                                    >
                                        {formik.values.state || "Select State"}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-100" style={{ maxHeight: "250px", overflowY: "auto" }}>
                                        <div className="px-2 py-2">
                                            <Form.Control
                                                type="text"
                                                placeholder="Search state..."
                                                value={stateSearch}
                                                onChange={(e) => setStateSearch(e.target.value)}
                                            />
                                        </div>
                                        <Dropdown.Divider />
                                        {filteredStates.length > 0 ? (
                                            filteredStates.map((state) => (
                                                <Dropdown.Item
                                                    key={state}
                                                    onClick={async () => {
                                                        await formik.setFieldValue("state", state);
                                                        await formik.setFieldTouched("state", true);
                                                    }}
                                                >
                                                    {state}
                                                </Dropdown.Item>
                                            ))
                                        ) : (
                                            <p className="px-3 text-muted mb-0">No results</p>
                                        )}
                                    </Dropdown.Menu>
                                </Dropdown>
                                {formik.touched.state && formik.errors.state && (
                                    <div className="invalid-feedback d-block">{formik.errors.state}</div>
                                )}
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">
                                    Postal Code <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    maxLength="5"
                                    className={`form-control ${formik.touched.postal_code && formik.errors.postal_code ? "is-invalid" : ""}`}
                                    placeholder="Enter postal code"
                                    {...formik.getFieldProps("postal_code")}
                                />
                                {formik.touched.postal_code && formik.errors.postal_code && (
                                    <div className="invalid-feedback">{formik.errors.postal_code}</div>
                                )}
                            </div>
                        </div>
                        <div className="d-flex gap-3">
                            <button type="submit" className="el-main-button" disabled={formik.isSubmitting}>
                                <span>{editingCampus ? "Update Campus" : "Save Campus"}</span>
                            </button>
                            <button
                                type="button"
                                className="back-link"
                                onClick={handleCancelEditing}
                                disabled={formik.isSubmitting}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>

                <div className="el-campus-table el-bg-table">
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <h3 className="mb-0">Campus List</h3>
                        <div className="d-flex align-items-center gap-3">
                            {filterCount > 0 && (
                                <span className="fw-semibold">
                                    {filterCount} {filterCount === 1 ? "filter" : "filters"} applied
                                </span>
                            )}
                            {filterCount > 0 && <ClearFilterButton handleClearFilters={handleClearFilters} />}
                        </div>
                    </div>

                    <div className="el-table-container el-board-table table-x-scroll">
                        <Table  hover className="table table-striped">
                            <thead>
                                <tr>
                                    <th>
                                        <SearchInput
                                            label="Campus Name"
                                            value={filters.campus_name}
                                            setValue={(val) => handleFilterInputChange("campus_name", val)}
                                            isVisible={searchVisibility.campus_name}
                                            setVisible={(isVisible) =>
                                                setSearchVisibility((prev) => ({ ...prev, campus_name: isVisible }))
                                            }
                                        />
                                    </th>
                                    <th>
                                        <SearchInput
                                            label="Pharma Name"
                                            value={filters.pharma_name}
                                            setValue={(val) => handleFilterInputChange("pharma_name", val)}
                                            isVisible={searchVisibility.pharma_name}
                                            setVisible={(isVisible) =>
                                                setSearchVisibility((prev) => ({ ...prev, pharma_name: isVisible }))
                                            }
                                        />
                                    </th>
                                    <th>
                                        <SearchInput
                                            label="Street Address 1"
                                            value={filters.address_1}
                                            setValue={(val) => handleFilterInputChange("address_1", val)}
                                            isVisible={searchVisibility.address_1}
                                            setVisible={(isVisible) =>
                                                setSearchVisibility((prev) => ({ ...prev, address_1: isVisible }))
                                            }
                                        />
                                    </th>
                                    <th>
                                        <SearchInput
                                            label="Street Address 2"
                                            value={filters.address_2}
                                            setValue={(val) => handleFilterInputChange("address_2", val)}
                                            isVisible={searchVisibility.address_2}
                                            setVisible={(isVisible) =>
                                                setSearchVisibility((prev) => ({ ...prev, address_2: isVisible }))
                                            }
                                        />
                                    </th>
                                    <th>
                                        <SearchInput
                                            label="City"
                                            value={filters.city}
                                            setValue={(val) => handleFilterInputChange("city", val)}
                                            isVisible={searchVisibility.city}
                                            setVisible={(isVisible) =>
                                                setSearchVisibility((prev) => ({ ...prev, city: isVisible }))
                                            }
                                        />
                                    </th>
                                    <th>
                                        <SearchInput
                                            label="State"
                                            value={filters.state}
                                            setValue={(val) => handleFilterInputChange("state", val)}
                                            isVisible={searchVisibility.state}
                                            setVisible={(isVisible) =>
                                                setSearchVisibility((prev) => ({ ...prev, state: isVisible }))
                                            }
                                        />
                                    </th>
                                    <th>
                                        <SearchInput
                                            label="Postal Code"
                                            value={filters.postal_code}
                                            setValue={(val) => handleFilterInputChange("postal_code", val)}
                                            isVisible={searchVisibility.postal_code}
                                            setVisible={(isVisible) =>
                                                setSearchVisibility((prev) => ({ ...prev, postal_code: isVisible }))
                                            }
                                        />
                                    </th>
                                    <th>
                                        <AdminStatusDropdown
                                            statuses={campusStatuses}
                                            selectedStatuses={selectedStatuses}
                                            handleStatusChange={handleStatusFilterChange}
                                            setSelectedStatuses={setSelectedStatuses}
                                            label="Status"
                                            type="sampleRequest"
                                        />
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campusList.length > 0 ? (
                                    campusList.map((campus) => (
                                        <tr key={campus.id || campus.campus_id}>
                                            <td>{campus.campus_name}</td>
                                            <td>{campus.pharma_name}</td>
                                            <td>{campus.campus_street1}</td>
                                            <td>{campus.campus_street2 || "-"}</td>
                                            <td>{campus.campus_city}</td>
                                            <td>{campus.campus_state}</td>
                                            <td>{campus.campus_postal_code}</td>
                                            <td>
                                                <div className={isCampusActive(campus) ? "el-active" : "el-inactive"}>
                                                    <div className={isCampusActive(campus) ? "el-activebtn" : "el-inactive"}></div>
                                                    {isCampusActive(campus) ? "Active" : "Inactive"}
                                                </div>
                                            </td>
                                            <td>
                                                <Dropdown>
                                                    <Dropdown.Toggle variant="" className="btn btn-primary dropdown-toggle">
                                                        <img src={navdots} alt="More actions" />
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu className="el-action">
                                                        <Dropdown.Item onClick={() => handleEditCampus(campus)}>
                                                            <label>Edit</label>
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => openStatusModal(campus)}>
                                                            <label className={isCampusActive(campus) ? "text-danger" : "text-success"}>
                                                                {isCampusActive(campus) ? "Deactivate" : "Activate"}
                                                            </label>
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={() => openDeleteModal(campus)}>
                                                            <label className="text-danger">Delete</label>
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    ))
                                ) : loading ? (
                                    <tr>
                                        <td colSpan="9" className="text-center py-4">
                                            Loading...
                                        </td>
                                    </tr>
                                ) : (
                                    <NoListDataAvailable type="campus" />
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <TablePagination
                        rowsPerPage={rowsPerPage || 10}
                        handleRowsPerPageChange={handleRowsPerPageChange}
                        pagination={pagination}
                        totalPages={totalPages}
                        handlePreviousPage={handlePreviousPage}
                        handleNextPage={handleNextPage}
                        totalUsers={totalCampus}
                    />
                </div>
            </div>

            <ConfirmationModal
                show={Boolean(actionModal.type)}
                onHide={closeModal}
                onConfirm={confirmModalAction}
                body={
                    actionModal.type === "delete"
                        ? "Are you sure you want to delete this campus?"
                        : `Are you sure you want to ${isCampusActive(actionModal.campus) ? "deactivate" : "activate"} this campus?`
                }
                type={actionModal.type === "delete" ? "delete" : "deactivateactivate"}
            />
            <Toaster
                show={toastState.show}
                message={toastState.message}
                variant={toastState.variant}
                onClose={closeToast}
            />
        </>
    );
}

export default AddCampus;